import { useState } from "react";
import {
	Box,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import DashboardOverview from "../../components/DashboardScreens/DashboardOverview";
import DashboardCustomers from "../../components/DashboardScreens/DashboardCustomers";
import { getBooleanValue } from "../../constants/CustomerConstants";
import { useUserContext } from "../../context/UserContext";

const SIDEBAR_WIDTH = 200;
const HEADER_HEIGHT = 64;

const DashboardLayout = ({ accountInfo, onThemeChange, onLogout, sidebarCollapsed, setOpen, setMetricsData }) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const { userDetails } = useUserContext();
	const isCustomerUser = getBooleanValue(userDetails.isCustomerUser);

	// Determine what name to display
	const displayName = isCustomerUser ? userDetails.username : accountInfo?.account?.name;
	const displayRole = isCustomerUser ? userDetails.customerName : "User";

	const [selectedView, setSelectedView] = useState(0);

	return (
		<Box
			sx={{
				display: "flex",
				height: `calc(100vh - ${HEADER_HEIGHT}px)`,
			}}
		>
			{/* Collapsible Sidebar */}
			{!sidebarCollapsed && (
				<Box
					sx={{
						width: SIDEBAR_WIDTH,
						flexShrink: 0,
						borderRight: 1,
						borderColor: "divider",
						backgroundColor: theme.palette.background.paper,
						display: "flex",
						flexDirection: "column",
					}}
				>
					{/* User Info */}
					<Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
						<PersonIcon />
						<Box>
							<Typography variant="body2" fontWeight="bold" sx={{ color: "#006098" }}>
								{displayName}
							</Typography>
							<Typography variant="caption">{displayRole}</Typography>
						</Box>
					</Box>

					<Divider />

					{/* Navigation List */}
					<List component="nav" sx={{ pt: 1 }}>
						<ListItemButton
							selected={selectedView === 0}
							onClick={() => setSelectedView(0)}
							sx={{
								minHeight: 48,
								px: 2.5,
							}}
						>
							<ListItemIcon
								sx={{
									minWidth: 0,
									mr: 2,
									justifyContent: "center",
								}}
							>
								<DashboardIcon />
							</ListItemIcon>
							<ListItemText primary="Overview" />
						</ListItemButton>

						{!isCustomerUser && (
							<ListItemButton
								selected={selectedView === 1}
								onClick={() => setSelectedView(1)}
								sx={{
									minHeight: 48,
									px: 2.5,
								}}
							>
								<ListItemIcon
									sx={{
										minWidth: 0,
										mr: 2,
										justifyContent: "center",
									}}
								>
									<PeopleIcon />
								</ListItemIcon>
								<ListItemText primary="Manage Customer" />
							</ListItemButton>
						)}

						<ListItemButton onClick={onThemeChange} sx={{ minHeight: 40, px: 2.5 }}>
							<ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: "center" }}>
								{isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
							</ListItemIcon>
							<ListItemText primary={isDarkMode ? "Light Mode" : "Dark Mode"} />
						</ListItemButton>

						{!isCustomerUser && (
							<ListItemButton onClick={() => setOpen(true)} sx={{ minHeight: 40, px: 2.5 }}>
								<ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: "center" }}>
									<PersonAddIcon />
								</ListItemIcon>
								<ListItemText primary="Register Customer" />
							</ListItemButton>
						)}

						<ListItemButton onClick={onLogout} sx={{ minHeight: 40, px: 2.5 }}>
							<ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: "center" }}>
								<LogoutIcon />
							</ListItemIcon>
							<ListItemText primary="Logout" />
						</ListItemButton>
					</List>
				</Box>
			)}

			{/* Content Area */}
			<Box
				sx={{
					flexGrow: 1,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					p: 0,
				}}
			>
				{selectedView === 0 ? (
					<DashboardOverview
						accountInfo={accountInfo}
						onThemeChange={onThemeChange}
						onLogout={onLogout}
						setMetricsData={setMetricsData}
					/>
				) : (
					<DashboardCustomers accountInfo={accountInfo} />
				)}
			</Box>
		</Box>
	);
};

export default DashboardLayout;
